/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React from 'react';

import { jsx } from '@compiled/react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin, ToolCall } from '../../../aiSuggestionsPluginType';
import { useSyncedSelectedSuggestion } from '../../hooks/useSyncedSelectedSuggestion';

import { IndicatorBar } from './IndicatorBar';

function getToolCallLocalIds(toolCall: ToolCall): Set<string> {
	const localIds = new Set<string>();

	if ('localId' in toolCall.arguments && toolCall.arguments.localId) {
		localIds.add(toolCall.arguments.localId);
	}

	if ('localIds' in toolCall.arguments) {
		for (const localId of toolCall.arguments.localIds) {
			localIds.add(localId);
		}
	}

	return localIds;
}

/**
 * We render this in a content component so that the IndicatorBar elements
 * share the same DOM context as their CSS anchor spans, allowing the browser to resolve
 * anchor-name references correctly.
 */
export const IndicatorBarContentComponent = ({
	api,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
}): JSX.Element | null => {
	const { selectedSuggestion } = useSyncedSelectedSuggestion(api);

	if (!selectedSuggestion?.toolCalls.length) {
		return null;
	}

	return (
		<>
			{selectedSuggestion.toolCalls.map((toolCall, toolCallIndex) =>
				Array.from(getToolCallLocalIds(toolCall)).map((localId) => (
					<IndicatorBar key={`${toolCallIndex}-${localId}`} localId={localId} />
				)),
			)}
		</>
	);
};
